import type { BillingPaymentDto, PaymentMethodDto } from "api/types";
import React from "react";
import { useTranslation } from "react-i18next";

import type { BillingPanelProps } from "../settings.types";

import CollapsibleSection from "./CollapsibleSection";
import InvoiceRowItem from "./InvoiceRowItem";

import styles from "./BillingPanel.module.css";

const formatDate = (iso?: string | null): string => {
	if (!iso) {
		return "";
	}

	const date = new Date(iso);
	if (Number.isNaN(date.getTime())) {
		return "";
	}

	return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

/** "Visa •••• 1007" for cards, the handle for UPI, otherwise the raw method name. */
const paymentInstrumentLabel = (method: PaymentMethodDto, cardFallback: string): string => {
	if (method.method === "card" && method.last4) {
		return `${method.network ?? cardFallback} •••• ${method.last4}`;
	}

	if (method.method === "upi" && method.upiHandle) {
		return method.upiHandle;
	}

	return method.method ? method.method.charAt(0).toUpperCase() + method.method.slice(1) : "";
};

const BillingPanel = ({
	billingDetails,
	downloadingPaymentId,
	errorMessage,
	isCancelling,
	isLoading,
	onCancelAutoRenew,
	onDownloadInvoice,
	onEditBillingAddress,
	onManagePlan,
	onUpgrade,
	paymentMethod,
	payments,
	subscription
}: BillingPanelProps) => {
	const { t } = useTranslation("settingsPage");

	if (isLoading) {
		return <p className={styles.loading}>{t("billing.loading")}</p>;
	}

	const isPro = subscription?.isPro ?? false;
	const autoRenewOn = isPro && subscription?.autoRenew !== false;
	const renewalDate = formatDate(subscription?.currentPeriodEnd ?? subscription?.renewalDate);

	let planCaption = t("billing.freeBlurb");
	if (isPro && autoRenewOn && renewalDate) {
		planCaption = t("billing.renewsOn", { date: renewalDate });
	} else if (isPro && !autoRenewOn) {
		planCaption = renewalDate ? t("billing.proUntil", { date: renewalDate }) : t("billing.autoRenewOff");
	}

	// Stays in the collapsed header so the address can be added/edited without expanding the card.
	const addressAction = (
		<button className={styles.secondaryButton} onClick={onEditBillingAddress} type="button">
			{billingDetails ? t("billing.updateAddress") : t("billing.addAddress")}
		</button>
	);

	return (
		<div className={styles.panel}>
			<section className={styles.planCard}>
				<div className={styles.planHead}>
					<div className={styles.planInfo}>
						<span className={`${styles.planBadge} ${isPro ? styles.planBadgePro : ""}`}>
							{isPro ? t("billing.proPlan") : t("billing.freePlan")}
						</span>
						<span className={styles.planPeriod}>{isPro ? t("billing.monthly") : t("billing.free")}</span>
						<p className={styles.planCaption}>{planCaption}</p>
					</div>
					<button className={styles.secondaryButton} onClick={onManagePlan} type="button">
						{t("billing.managePlan")}
					</button>
				</div>

				<div className={styles.planActions}>
					{!isPro && (
						<button className={styles.primaryButton} onClick={onUpgrade} type="button">
							{t("billing.upgrade")}
						</button>
					)}
				</div>
			</section>

			{paymentMethod && (
				<section className={styles.sectionCard}>
					<h3 className={styles.cardTitle}>{t("billing.payment")}</h3>
					<div className={styles.paymentRow}>
						<span className={styles.paymentBadge}>
							{(paymentMethod.method === "card" ? paymentMethod.network : paymentMethod.method) ??
								paymentMethod.method}
						</span>
						<span className={styles.paymentText}>
							{paymentInstrumentLabel(paymentMethod, t("billing.card"))}
						</span>
					</div>
				</section>
			)}

			{payments.length > 0 && (
				<CollapsibleSection title={t("billing.invoices")} defaultExpanded>
					<div className={styles.invoiceTableWrap}>
						<table className={styles.invoiceTable}>
							<thead>
								<tr>
									<th className={styles.invoiceHeadCell}>{t("billing.dateColumn")}</th>
									<th className={styles.invoiceHeadCell}>{t("billing.totalColumn")}</th>
									<th className={styles.invoiceHeadCell}>{t("billing.statusColumn")}</th>
									<th className={styles.invoiceHeadCell}>{t("billing.actionsColumn")}</th>
								</tr>
							</thead>
							<tbody>
								{payments.map((payment: BillingPaymentDto) => (
									<InvoiceRowItem
										key={payment.id}
										isDownloading={downloadingPaymentId === payment.id}
										onDownload={onDownloadInvoice}
										payment={payment}
									/>
								))}
							</tbody>
						</table>
					</div>
				</CollapsibleSection>
			)}

			<CollapsibleSection headerAction={addressAction} title={t("billing.billingAddress")}>
				{billingDetails ? (
					<address className={styles.address}>
						<span className={styles.addressName}>{billingDetails.customerName}</span>
						<span>{billingDetails.billingAddress}</span>
						<span>
							{[billingDetails.city, billingDetails.state, billingDetails.pinCode]
								.filter(Boolean)
								.join(", ")}
						</span>
						<span>{billingDetails.country}</span>
					</address>
				) : (
					<p className={styles.addressEmpty}>{t("billing.noAddress")}</p>
				)}
			</CollapsibleSection>

			{autoRenewOn && (
				<section className={styles.sectionCard}>
					<h3 className={styles.cardTitle}>{t("billing.cancellation")}</h3>
					<div className={styles.cancelRow}>
						<span className={styles.cancelLabel}>{t("billing.cancelPlan")}</span>
						<button
							className={styles.dangerButton}
							disabled={isCancelling}
							onClick={onCancelAutoRenew}
							type="button">
							{isCancelling ? t("billing.cancelling") : t("billing.cancel")}
						</button>
					</div>
				</section>
			)}

			{errorMessage && <p className={styles.error}>{errorMessage}</p>}
		</div>
	);
};

export default BillingPanel;
